import {
    TABLE_BODY_WIDTH,
    TABLE_EDIT_HEADER_TEXT_OFFSET,
    TABLE_EDIT_TITLE_TEXT_OFFSET,
    TABLE_EDIT_TITLE_TOP_PADDING,
    TABLE_TITLE_END_PADDING,
    TABLE_TITLE_TEXT_OFFSET,
} from "canvas/table/geometry";
import { EditTable } from "diagram/model/edit/edit";
import { RenameTablePayload } from "diagram/state/reducer/table/rename";
import { DiagramProps } from "diagram/state/state";
import { Point } from "geometry/point";
import { TextArea } from "input/textarea";
import * as React from "react";
import { TableExpandedProps } from "../expand";

interface Props extends TableExpandedProps {
    table: EditTable;
    diagramDispatch: DiagramProps["diagramDispatch"];
}

const TITLE_EDIT_TEXTAREA_TEXT_INSET = 7;

const dispatchRename = (props: Props, value: string) => {
    if (value === props.table.value.name) return;
    const payload: RenameTablePayload = { id: props.table.id, name: value };
    props.diagramDispatch({ type: "rename-table", payload });
};

const View = (props: Props & { state: State }) => (
    <div
        className={["leading-normal break-words whitespace-pre-wrap"].join(" ")}
        onMouseDown={(event) => {
            props.state.offset.current.x = event.clientX;
            props.state.offset.current.y = event.clientY;
        }}
        onClick={(event) => {
            const { x: x1, y: y1 } = props.state.offset.current;
            const { clientX: x2, clientY: y2 } = event;
            // Only enter edit mode if this is not a drag
            if (x1 === x2 && y1 === y2) props.state.setIsEdit(true);
        }}
        children={props.table.value.name}
    />
);

const Edit = (props: Props & { state: State }) => {
    const textareaRef = React.useRef<HTMLTextAreaElement>(null);

    const syncHeight = React.useCallback(() => {
        const el = textareaRef.current;
        if (!el) return;
        el.style.height = "auto";
        el.style.height = `${el.scrollHeight}px`;
    }, []);

    React.useLayoutEffect(() => {
        syncHeight();
        const el = textareaRef.current;
        if (!el) return;
        el.focus();
        const end = el.value.length;
        el.setSelectionRange(end, end);
    }, [syncHeight]);

    return (
        <TextArea
            ref={textareaRef}
            key={props.table.value.name} // uncontrolled
            rows={1}
            paddingStyle="py-0 px-1.5"
            defaultValue={props.table.value.name}
            onValueChange={syncHeight}
            onBlur={(event) => {
                dispatchRename(props, event.currentTarget.value);
                props.state.setIsEdit(false);
            }}
            onFocus={() => {
                props.setTableExpanded(false);
            }}
            onKeyDown={(event) => {
                if (event.key === "Enter" && (event.metaKey || event.ctrlKey)) {
                    event.preventDefault();
                    dispatchRename(props, event.currentTarget.value);
                    props.state.setIsEdit(false);
                }
            }}
            extraStyle={[
                "text-left font-semibold leading-normal resize-none",
                "overflow-hidden break-words whitespace-pre-wrap",
            ].join(" ")}
            style={{
                marginLeft: -TITLE_EDIT_TEXTAREA_TEXT_INSET,
                width: `calc(100% + ${TITLE_EDIT_TEXTAREA_TEXT_INSET}px)`,
            }}
        />
    );
};

interface State {
    setIsEdit: React.Dispatch<React.SetStateAction<boolean>>;
    offset: React.MutableRefObject<Point>;
}

export const TableTitle = (props: Props) => {
    // View vs edit (textarea) so users can drag the table from the title
    // without entering edit mode on every mousedown
    const [isEdit, setIsEdit] = React.useState(false);

    // Because it is draggable, this helps us identify if a drag happened or
    // just a click to prevent entering edit mode when it's a drag
    const offset = React.useRef({ x: 0, y: 0 });

    const state: State = { setIsEdit, offset };
    const style = {
        paddingLeft: props.tableExpanded
            ? TABLE_EDIT_TITLE_TEXT_OFFSET
            : TABLE_TITLE_TEXT_OFFSET,
        paddingRight: props.tableExpanded
            ? TABLE_EDIT_HEADER_TEXT_OFFSET
            : TABLE_TITLE_END_PADDING,
        paddingTop: props.tableExpanded
            ? TABLE_EDIT_TITLE_TOP_PADDING
            : undefined,
    };

    return (
        <div
            className={[
                "p-2 relative diagram-table-title rounded-t-md",
                "text-[var(--diagram-table-text)]",
            ].join(" ")}
            style={style}
        >
            <div
                className={[
                    "transition-transform text-left font-semibold flex-0",
                    props.tableExpanded ? "-translate-y-1" : "",
                ].join(" ")}
                style={{ width: TABLE_BODY_WIDTH }}
            >
                {isEdit ? (
                    <Edit {...props} state={state} />
                ) : (
                    <View {...props} state={state} />
                )}
            </div>
        </div>
    );
};
